import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

import { useMe } from '@/platform/auth/use-me';
import {
  brandDisplayName,
  publicBrandLogoSrc,
  usePublicBrand,
} from '@/platform/brand/use-public-brand';
import { appLogoPath } from '@/platform/lib/post-login';
import { cn } from '@/lib/utils';

type AppLogoLinkProps = {
  className?: string;
  imageClassName?: string;
  /** Shown next to the logo inside the same link (Mitarbeiterportal chrome). */
  title?: ReactNode;
  'aria-label'?: string;
};

export function AppLogoLink({
  className,
  imageClassName = 'h-8 w-auto sm:h-9',
  title,
  'aria-label': ariaLabel = 'Zur Startseite',
}: AppLogoLinkProps) {
  const me = useMe();
  const brand = usePublicBrand();
  const to = appLogoPath(me.data);
  const name = brandDisplayName(brand.data);
  const logoSrc = publicBrandLogoSrc(brand.data?.logoUrl);
  const showNameFallback = Boolean(name) && !title;

  return (
    <NavLink
      to={to}
      className={cn('flex shrink-0 items-center gap-2.5 font-semibold text-primary', className)}
      aria-label={title ? undefined : ariaLabel}
    >
      {logoSrc ? (
        <img
          src={logoSrc}
          alt={title ? '' : name || ariaLabel}
          className={cn('w-auto', imageClassName)}
        />
      ) : showNameFallback ? (
        <span className={cn('max-w-[10rem] truncate sm:max-w-xs', imageClassName)}>{name}</span>
      ) : null}
      {title}
    </NavLink>
  );
}
